import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { listLeads, exportLeadsCsvUrl } from "@/lib/api";
import {
  Loader2,
  RefreshCw,
  Phone,
  MessageCircle,
  Download,
  CalendarRange,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}
const todayISO = () => new Date().toISOString().slice(0, 10);
const daysAgoISO = (n) => {
  const d = new Date(); d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

export default function AdminLeads() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");

  const load = async () => {
    setLoading(true); setErr(null);
    try {
      const data = await listLeads({
        start: start || undefined,
        end: end || undefined,
      });
      setRows(data);
    } catch (e) {
      if (e?.response?.status === 401) { navigate("/login", { replace: true }); return; }
      setErr(e.message || "Failed to load leads");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const applyPreset = (days) => { setStart(daysAgoISO(days)); setEnd(todayISO()); };
  const resetFilter = () => { setStart(""); setEnd(""); };

  return (
    <div data-testid="admin-leads">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-display font-bold text-2xl text-ink">Leads</h2>
          <p className="text-sm text-muted-ink mt-1">
            {loading ? "Loading..." : `${rows.length} lead${rows.length === 1 ? "" : "s"} shown`}
          </p>
        </div>
      </div>

      <div className="mt-4 rounded-2xl bg-white hairline shadow-soft p-5 md:p-6">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <CalendarRange className="h-4 w-4 text-orange-600" />
            <div className="text-[11px] tracking-[0.2em] uppercase font-bold text-muted-ink">Date range</div>
          </div>
          <div className="flex gap-2 ml-auto flex-wrap">
            {[{ label: "7d", days: 7 }, { label: "30d", days: 30 }, { label: "90d", days: 90 }].map((p) => (
              <button
                key={p.days}
                onClick={() => applyPreset(p.days)}
                className="rounded-full hairline bg-white hover:bg-orange-50 px-3 py-1 text-xs font-bold text-ink"
                data-testid={`admin-preset-${p.days}`}
              >
                Last {p.label}
              </button>
            ))}
            {(start || end) && (
              <button
                onClick={resetFilter}
                className="rounded-full bg-slate-100 hover:bg-slate-200 px-3 py-1 text-xs font-bold text-ink inline-flex items-center gap-1"
                data-testid="admin-clear-filter"
              >
                <X className="h-3 w-3" /> Clear
              </button>
            )}
          </div>
        </div>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-[1fr_1fr_auto_auto] gap-3 items-end">
          <div>
            <Label htmlFor="start" className="text-xs font-bold text-ink">Start date</Label>
            <Input id="start" type="date" value={start} onChange={(e) => setStart(e.target.value)}
                   className="mt-1.5 h-11 rounded-xl" data-testid="admin-start-date" />
          </div>
          <div>
            <Label htmlFor="end" className="text-xs font-bold text-ink">End date</Label>
            <Input id="end" type="date" value={end} onChange={(e) => setEnd(e.target.value)}
                   className="mt-1.5 h-11 rounded-xl" data-testid="admin-end-date" />
          </div>
          <Button onClick={load} className="h-11 rounded-full bg-ink hover:bg-slate-800 text-white font-bold"
                  data-testid="admin-apply-filter">
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} />
            Apply
          </Button>
          <a
            href={exportLeadsCsvUrl({ start: start || undefined, end: end || undefined })}
            data-testid="admin-export-csv"
            className="h-11 rounded-full bg-orange-500 hover:bg-orange-600 text-white font-bold inline-flex items-center justify-center px-5 shadow-brand"
          >
            <Download className="h-4 w-4 mr-1.5" /> Export CSV
          </a>
        </div>
      </div>

      <div className="mt-6 rounded-2xl bg-white hairline shadow-soft overflow-hidden">
        {loading ? (
          <div className="p-16 grid place-items-center text-muted-ink">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : err ? (
          <div className="p-10 text-center text-red-600 font-semibold" data-testid="admin-error">{err}</div>
        ) : rows.length === 0 ? (
          <div className="p-16 text-center text-muted-ink" data-testid="admin-empty">
            No leads in the selected range.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table data-testid="admin-leads-table">
              <TableHeader>
                <TableRow className="bg-slate-50/60">
                  <TableHead>When</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Project</TableHead>
                  <TableHead>Area</TableHead>
                  <TableHead>Site visit</TableHead>
                  <TableHead>Est. Cost</TableHead>
                  <TableHead>Promo</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id} data-testid={`lead-row-${r.id}`}>
                    <TableCell className="text-xs whitespace-nowrap text-muted-ink">{formatDate(r.created_at)}</TableCell>
                    <TableCell className="font-bold text-ink">{r.name}</TableCell>
                    <TableCell className="font-mono text-sm">{r.phone}</TableCell>
                    <TableCell className="text-sm">{r.project_type}</TableCell>
                    <TableCell className="text-sm">{r.area || "—"}</TableCell>
                    <TableCell className="text-sm whitespace-nowrap" data-testid={`lead-visit-${r.id}`}>
                      {r.preferred_date
                        ? `${r.preferred_date}${r.preferred_slot ? ` · ${r.preferred_slot}` : ""}`
                        : "—"}
                    </TableCell>
                    <TableCell className="text-sm">
                      {r.estimated_cost
                        ? `₹${new Intl.NumberFormat("en-IN").format(r.estimated_cost)}`
                        : "—"}
                    </TableCell>
                    <TableCell className="font-mono text-[11px]">
                      {r.promo_code || "—"}
                    </TableCell>
                    <TableCell>
                      <span className="inline-flex rounded-full bg-orange-50 text-orange-700 text-[11px] font-bold uppercase tracking-wider px-2 py-0.5">
                        {r.source || "website"}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1.5">
                        <a href={`tel:${r.phone}`} className="h-8 w-8 rounded-lg grid place-items-center bg-slate-100 hover:bg-slate-200" title="Call">
                          <Phone className="h-4 w-4 text-ink" />
                        </a>
                        <a href={`https://wa.me/${r.phone.replace(/[^\d]/g, "")}`} target="_blank" rel="noreferrer"
                           className="h-8 w-8 rounded-lg grid place-items-center bg-green-100 hover:bg-green-200" title="WhatsApp">
                          <MessageCircle className="h-4 w-4 text-green-700" />
                        </a>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}
